import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Link } from 'react-router-dom';

import { withRouter } from 'utils';
import { DateUtil, StoreUtil } from 'doctivity-shared/utils';
import { getStarkLog } from 'store/actions/starkActions';
import { listActiveUsers } from 'store/actions/usersActions';

import StarkDialog from './StarkDialog';
import { STARK_CATEGORY_MAP } from './starkConstants';

const styles = (theme) => ({
  content: {
    padding: theme.spacing(3),
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1),
  },
  cardTitle: {
    flex: 1,
    fontWeight: 600,
    color: theme.palette.primary.dark,
  },
  editButton: {
    color: theme.palette.secondary.main,
  },
  leftIcon: {
    marginRight: theme.spacing(0.5),
  },
  row: {
    display: 'flex',
    marginBottom: theme.spacing(1.5),
  },
  label: {
    width: 180,
    color: theme.palette.text.secondary,
    fontSize: 13,
    fontWeight: 500,
    flexShrink: 0,
  },
  value: {
    fontSize: 14,
  },
  loadingBox: {
    padding: theme.spacing(6),
    display: 'flex',
    justifyContent: 'center',
  },
  allocTable: {
    marginTop: theme.spacing(1),
    '& .MuiTableCell-root': {
      padding: theme.spacing(0.75, 1),
      fontSize: 13,
    },
    '& .MuiTableCell-head': {
      fontSize: 12,
      fontWeight: 500,
      color: theme.palette.text.secondary,
    },
  },
});

class StarkLogDetailPage extends Component {
  constructor(props) {
    super(props);
    this.state = { editDialogOpen: false };
  }

  componentDidMount() {
    this._loadLog();
    this._loadUsers();
  }

  componentDidUpdate(prevProps) {
    const { router, app } = this.props;
    const id = router.params.id;
    if (
      id !== prevProps.router.params.id ||
      app.selectedClient !== prevProps.app.selectedClient
    ) {
      this._loadLog();
    }
    if (app.selectedClient !== prevProps.app.selectedClient) {
      this._loadUsers();
    }
  }

  _loadLog() {
    const { dispatch, router } = this.props;
    const id = router.params.id;
    if (id) dispatch(getStarkLog(id));
  }

  // Admins need the client's user list to reassign an entry on edit.
  _loadUsers() {
    const { dispatch, isAdmin, app } = this.props;
    if (isAdmin && app.selectedClient) dispatch(listActiveUsers(app.selectedClient));
  }

  render() {
    const { classes, log, loading, app, isAdmin, currentUserId } = this.props;
    const { editDialogOpen } = this.state;

    if (loading) {
      return (
        <Box className={classes.loadingBox}>
          <CircularProgress />
        </Box>
      );
    }

    if (!log) {
      return (
        <Box sx={{ p: 4 }}>
          <Typography color='error'>Non-monetary compensation record not found.</Typography>
          <Button component={Link} to='/notes/stark' sx={{ mt: 2 }}>
            ← Back to Stark Tracking
          </Button>
        </Box>
      );
    }

    const isOwner        = log.user_id === currentUserId;
    // Owners edit their own entries; admins may edit/reassign any entry in the
    // client. The Jan-30 lock applies to everyone, admins included.
    const canEdit        = !log.locked && (isOwner || isAdmin);

    const Field = ({ label, value }) => (
      <div className={classes.row}>
        <Typography className={classes.label}>{label}</Typography>
        <Typography className={classes.value} component='div'>{value || '—'}</Typography>
      </div>
    );

    const categoryLabel = STARK_CATEGORY_MAP[log.category] || `Category ${log.category}`;
    const spendingDate  = log.spending_date
      ? DateUtil.formatDateForUser(log.spending_date + 'T00:00:00')
      : '—';

    return (
      <>
        <title>Stark Entry — {spendingDate}</title>

        <div className={classes.content}>
          <Card className={classes.card}>
            <CardContent>
              <div className={classes.cardHeader}>
                <Typography className={classes.cardTitle}>
                  Non-Monetary Compensation Entry
                </Typography>
                {canEdit && (
                  <Button
                    variant='text'
                    className={classes.editButton}
                    onClick={() => this.setState({ editDialogOpen: true })}
                  >
                    <EditOutlinedIcon className={classes.leftIcon} />
                    Edit
                  </Button>
                )}
              </div>

              <Field label='Spending Date' value={spendingDate} />
              <Field label='Category'      value={categoryLabel} />
              <Field label='Total Amount'  value={`$${parseFloat(log.total_amount || 0).toFixed(2)}`} />
              {log.description && (
                <Field label='Description' value={log.description} />
              )}

              <Divider sx={{ my: 1.5 }} />

              {/* Per-provider allocation breakdown */}
              {(log.allocations || []).length > 0 && (
                <>
                  <Typography className={classes.label} sx={{ mb: 1 }}>
                    Provider Allocations
                  </Typography>
                  <Table size='small' className={classes.allocTable}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Provider</TableCell>
                        <TableCell>NPI</TableCell>
                        <TableCell align='right'>Allocated</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {log.allocations.map((alloc) => {
                        const prov = alloc.provider;
                        const name = prov
                          ? `${prov.last_name}, ${prov.first_name}`.toUpperCase()
                          : `Provider #${alloc.provider_id}`;
                        return (
                          <TableRow key={alloc.provider_id}>
                            <TableCell>
                              {prov ? (
                                <Button
                                  component={Link}
                                  to={`/providers/${prov.id}/stark`}
                                  size='small'
                                  sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 400 }}
                                >
                                  {name}
                                </Button>
                              ) : name}
                            </TableCell>
                            <TableCell>{prov?.npi || '—'}</TableCell>
                            <TableCell align='right'>
                              ${parseFloat(alloc.allocated_amount || 0).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <Divider sx={{ my: 1.5 }} />
                </>
              )}

              {log.user && (
                <Field
                  label='Assigned To'
                  value={`${log.user.first_name} ${log.user.last_name}`}
                />
              )}

              {/* Link back to the associated activity note */}
              {log.activity_note && (
                <Field
                  label='Activity Note'
                  value={
                    <Button
                      component={Link}
                      to={`/notes/${log.activity_note.id}`}
                      size='small'
                      sx={{ p: 0, minWidth: 0 }}
                    >
                      {DateUtil.formatDateTimeFromDB(log.activity_note.comment_date)}
                    </Button>
                  }
                />
              )}

              {log.locked && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant='caption' color='warning.main'>
                    This entry is from a prior year and is past the Jan 30 edit window.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Edit dialog */}
        {editDialogOpen && (
          <StarkDialog
            open={editDialogOpen}
            log={log}
            clientId={log.client_id}
            isAdmin={isAdmin}
            clientUsers={this.props.clientUsers}
            providers={(log.allocations || [])
              .filter((a) => a.provider)
              .map((a) => a.provider)}
            onClose={() => this.setState({ editDialogOpen: false })}
            onSaved={() => {
              this.setState({ editDialogOpen: false });
              this._loadLog();
            }}
            onDeleted={() => {
              this.setState({ editDialogOpen: false });
              this.props.router.navigate('/notes/stark');
            }}
          />
        )}

      </>
    );
  }
}

StarkLogDetailPage.propTypes = {
  classes:       PropTypes.object.isRequired,
  dispatch:      PropTypes.func.isRequired,
  router:        PropTypes.object.isRequired,
  app:           PropTypes.object.isRequired,
  log:           PropTypes.object,
  loading:       PropTypes.bool,
  isAdmin:       PropTypes.bool,
  currentUserId: PropTypes.number,
  clientUsers:   PropTypes.array,
};

function mapStateToProps(state) {
  const usersStore  = StoreUtil.get(state.users, StoreUtil.COMMON_LIST);
  const clientUsers = StoreUtil.isLoaded(usersStore) ? (usersStore.data || []) : [];
  return {
    app:           state.app,
    log:           state.stark?.log?.data || null,
    loading:       state.stark?.log?.loading || false,
    isAdmin:       state.user?.is_admin || false,
    currentUserId: state.user?.id || null,
    clientUsers,
  };
}

export default connect(mapStateToProps)(withRouter(withStyles(styles)(StarkLogDetailPage)));
